import axios from 'axios';
import { API_URL } from '../config/api';
import SessionService from './SessionService';
import appCache from './AppCache';
import type { UserModules } from './ModuleService';
import { normalizeUserModules, clearModulesCache, seedModulesCache } from './ModuleService';
import type { Sector } from './SectorService';
import type { DashboardStatsDTO } from '../types/pagedAndDtos';

const SECTOR_ACCESS_DENIED_CODE = 'SECTOR_ACCESS_DENIED';

const BOOTSTRAP_TTL_MS = 5 * 60 * 1000;
const SECTORS_TTL_MS = 10 * 60 * 1000;

export interface SectorBootstrapData {
  sectorId: number;
  dashboardStats: DashboardStatsDTO;
  tags: unknown;
  contactsFirstPage: unknown;
}

export interface BootstrapPayload {
  profile: unknown;
  modules: UserModules;
  sectors: { data: Sector[]; message?: string; code?: string };
  subscription: unknown;
  sectorData?: SectorBootstrapData | null;
  cachedAtUnix: number;
  fromCache?: boolean;
}

export function hydrateAppCacheFromBootstrap(data: BootstrapPayload): void {
  if (data.profile) appCache.set('profile', data.profile, BOOTSTRAP_TTL_MS);
  if (data.modules) {
    const modules = seedModulesCache(normalizeUserModules(data.modules));
    window.dispatchEvent(new CustomEvent('modulesChanged', { detail: modules }));
  }
  if (data.sectors) appCache.set('sectors', data.sectors, SECTORS_TTL_MS);
  if (data.subscription !== undefined) appCache.set('subscription', data.subscription, BOOTSTRAP_TTL_MS);

  if (data.sectorData?.sectorId) {
    const sid = data.sectorData.sectorId;
    if (data.sectorData.dashboardStats) {
      appCache.set(`dashboard:${sid}`, data.sectorData.dashboardStats, BOOTSTRAP_TTL_MS);
    }
    if (data.sectorData.tags) {
      appCache.set(`tags:${sid}`, data.sectorData.tags, BOOTSTRAP_TTL_MS);
    }
    if (data.sectorData.contactsFirstPage) {
      appCache.set(`contacts:${sid}:page:1`, data.sectorData.contactsFirstPage, BOOTSTRAP_TTL_MS);
    }
  }

  appCache.set('bootstrap', data, BOOTSTRAP_TTL_MS);
}

export async function prefetchSessionBootstrap(sectorId?: number | null): Promise<BootstrapPayload> {
  const token = SessionService.getToken();
  if (!token) throw new Error('Authentication token not found');

  try {
    const response = await axios.get<BootstrapPayload>(`${API_URL}/session/bootstrap`, {
      params: sectorId && sectorId > 0 ? { sectorId } : undefined,
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });

    hydrateAppCacheFromBootstrap(response.data);
    return response.data;
  } catch (error) {
    if (
      axios.isAxiosError(error) &&
      error.response?.status === 403 &&
      error.response?.data?.code === SECTOR_ACCESS_DENIED_CODE &&
      sectorId
    ) {
      SessionService.revokeSectorSelection(sectorId);
      window.dispatchEvent(new CustomEvent('sectorAccessDenied'));
    }
    throw error;
  }
}

export function clearAppCache(): void {
  clearModulesCache();
  appCache.clear();
}